import { computed, ref } from "vue";
import { Buffer } from "buffer";
import { useI18n } from "vue-i18n";
import * as Yup from "yup";
import { useField, useForm } from "vee-validate";

export default function useTelemetryConfig() {
  const TAGS = {
    FQDN: "80",
    PORT: "81",
    INTERVAL: "83",
    ENCRYPT: "84",
    POLLING: "85",
    C9: "C9",
    EA: "EA",
    MENU: "80",
  } as const;
  const { t } = useI18n();
  const encryptionItems = computed(() => {
    return [
      {
        title: t("ota.header.Enable"),
        value: true,
      },
      {
        title: t("ota.header.Disable"),
        value: false,
      },
    ];
  });
  const numberErrorMessages = computed(() => {
    return {
      port: t("ota.validate.invalidNumber", { min: 1, max: 65535 }),
      hours: t("ota.validate.invalidNumber", { min: 0, max: 99 }),
      minutes: t("ota.validate.invalidNumber", { min: 0, max: 59 }),
    };
  });
  const getTestConfig = (name: string, errorMessage: string, min: number, max: number) => {
    const testConfig: Yup.TestConfig<string | number | undefined> = {
      name,
      test: function (value) {
        if (`${Number(value)}` !== value || Number(value) < min || Number(value) > max) {
          return false;
        }
        return true;
      },
      message: errorMessage,
    };
    return testConfig;
  };
  const invalidLengthMessage = computed(() => t("ota.validate.InvalidLength", { min: 1, max: 45 }));
  const schema = Yup.object({
    fqdn: Yup.string()
      .min(1, invalidLengthMessage.value)
      .max(45, invalidLengthMessage.value)
      .matches(/^[0-9a-zA-Z.-]*$/, t("ota.validate.InvalidCharacter")),
    port: Yup.number().min(1, numberErrorMessages.value.port).max(65535, numberErrorMessages.value.port).typeError(numberErrorMessages.value.port),
    reportIntervalHours: Yup.string()
      .required(t("RequiredError", { label: t("ota.header.hours") }))
      .test(getTestConfig("InvalidHour", numberErrorMessages.value.hours, 0, 99)),
    reportIntervalMinutes: Yup.string()
      .required(t("RequiredError", { label: t("ota.header.minutes") }))
      .test(getTestConfig("InvalidMinutes", numberErrorMessages.value.minutes, 0, 59)),
    enableEncryption: Yup.boolean().required(t("RequiredError", { label: t("ota.header.EncryptTransmissionData") })),
  });
  const { meta } = useForm({
    validationSchema: schema,
    initialValues: {
      fqdn: `proxy.${process.env.VUE_APP_ENV}.${process.env.VUE_APP_DOMAIN_NAME}`,
      port: 80,
      reportIntervalHours: 0,
      reportIntervalMinutes: 5,
      enableEncryption: true,
    },
  });
  const { value: fqdn, errorMessage: fqdnError } = useField<string>("fqdn");
  const { value: port, errorMessage: portError } = useField<number>("port");
  const { value: reportIntervalHours, errorMessage: reportIntervalHoursError } = useField<number>("reportIntervalHours");
  const { value: reportIntervalMinutes, errorMessage: reportIntervalMinutesError } = useField<number>("reportIntervalMinutes");
  const isEnablePolling = ref(true);
  const isEnableMenuDisplay = ref(true);
  const _reportIntervalHoursError = computed(() => {
    if (!isEnablePolling.value) {
      return;
    }
    if (reportIntervalHoursError.value) {
      return reportIntervalHoursError.value;
    }
    if (String(reportIntervalHours.value) === "0" && Number(`${reportIntervalMinutes.value}`) < 5) {
      return t("ota.validate.InvalidIntervalTotalValue");
    }
    if (!/^[0-9]*$/.test(String(reportIntervalHours.value))) {
      return t("ota.validate.InvalidInt");
    }
    return undefined;
  });
  const _reportIntervalMinutesError = computed(() => {
    if (!isEnablePolling.value) {
      return;
    }
    if (reportIntervalMinutesError.value) {
      return reportIntervalMinutesError.value;
    }
    if (String(reportIntervalHours.value) === "0" && Number(`${reportIntervalMinutes.value}`) < 5) {
      return t("ota.validate.InvalidIntervalTotalValue");
    }
    if (!/^[0-9]*$/.test(String(reportIntervalMinutes.value))) {
      return t("ota.validate.InvalidInt");
    }
    return undefined;
  });
  const { value: enableEncryption, errorMessage: enableEncryptionError } = useField<boolean>("enableEncryption");
  const isValid = computed(() => {
    if (isEnablePolling.value) {
      return !fqdnError.value && !portError.value && !_reportIntervalHoursError.value && !_reportIntervalMinutesError.value;
    } else {
      return !fqdnError.value && !portError.value;
    }
  });

  const isHex = (input: string): boolean => {
    const res = input.match(/[0-9a-fA-F]*/);
    if (res && res[0] === input && input.length % 2 === 0) {
      return true;
    }
    return false;
  };

  const initByInstallParameters = (params: string, swapReportInterval = false) => {
    if (!params || !isHex(params)) {
      return;
    }
    const buffer = Buffer.from(params, "hex");
    if (buffer[0].toString(16).toUpperCase() !== TAGS.C9) {
      return;
    }
    // NOTE: tagとlengthの長さ
    const tlLength = 2;
    const len = buffer[1];
    const data = buffer.slice(tlLength, len + tlLength);
    parseApplicationSpecificParameters(data, len, swapReportInterval);

    const eaIndex = buffer.findIndex((value) => value.toString(16).toUpperCase() === TAGS.EA);
    if (eaIndex >= 0) {
      const len = buffer[eaIndex + 1];
      const data = buffer.slice(eaIndex + tlLength, eaIndex + len + tlLength);
      parseEAParameters(data, len);
    }
  };

  const parseEAParameters = (params: Buffer, length: number) => {
    let len = 0;
    const tlLen = 2;
    for (let offset = 0; offset < length; offset += len + tlLen) {
      const tag = params[offset].toString(16).toUpperCase();
      len = parseInt(params[offset + 1].toString(), 10);
      const data = params.slice(offset + tlLen, offset + len + tlLen);
      switch (tag) {
        case TAGS.MENU: {
          // NOTE: 4つ目(インデックスが3)の値がメニュー表示数
          const menus = data[3];
          isEnableMenuDisplay.value = menus > 0;
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const parseApplicationSpecificParameters = (params: Buffer, length: number, swapReportInterval = false) => {
    let len = 0;
    const tlLen = 2;
    for (let offset = 0; offset < length; offset += len + tlLen) {
      const tag = params[offset].toString(16).toUpperCase();
      len = parseInt(params[offset + 1].toString(), 10);
      const data = params.slice(offset + tlLen, offset + len + tlLen);
      switch (tag) {
        case TAGS.FQDN: {
          if (len >= 5 && len <= 127) {
            // NOTE: asciiコードから文字列に変換
            fqdn.value = data.reduce((str, buf) => {
              str += String.fromCharCode(buf);
              return str;
            }, "");
          }
          break;
        }
        case TAGS.PORT: {
          if (len === 2) {
            port.value = parseInt(data.toString("hex"), 16);
          }
          break;
        }
        case TAGS.INTERVAL: {
          if (len === 3) {
            reportIntervalHours.value = parseInt(data[0].toString(16));
            reportIntervalMinutes.value = parseInt(data[1].toString(16));
            if (swapReportInterval) {
              // NOTE: timerは使わなくなるが整合性を取る為にオプションでswapした値をセットできるようにしておく
              reportIntervalHours.value = parseInt(swap(padding(reportIntervalHours.value.toString())));
              reportIntervalMinutes.value = parseInt(swap(padding(reportIntervalMinutes.value.toString())));
            }
          }
          break;
        }
        case TAGS.ENCRYPT: {
          if (len === 1) {
            enableEncryption.value = data[0] === 1;
          }
          break;
        }
        case TAGS.POLLING: {
          if (len === 3) {
            isEnablePolling.value = [1, 2].includes(data[0]);
            if (data[0] === 0) {
              // ポーリング無効の場合
              reportIntervalHours.value = 0;
              reportIntervalMinutes.value = 0;
            } else if (data[0] === 1) {
              // ポーリングを周期をminutesで指定している場合
              const totalMinutes = parseInt(data[1].toString(16) + data[2].toString(16), 16);
              // 時間と分に分割
              const hours = Math.floor(totalMinutes / 60);
              reportIntervalHours.value = hours;
              reportIntervalMinutes.value = totalMinutes - hours * 60;
            } else if (data[0] === 2) {
              // ポーリング周期をhoursで指定している場合
              reportIntervalHours.value = parseInt(data[1].toString(16) + data[2].toString(16), 16);
              reportIntervalMinutes.value = 0;
            }
          }
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const buildPollingValue = () => {
    /**
     * 1桁目: pollingの有無・周期の単位を指定
     *  0: 端末起動時のみpollingを行う
     *  1: 指定周期でpollingを行う(単位=分)
     *  2: 指定周期でpollingを行う(単位=hours)(UI的に使用してない)
     * 2・3桁目: polling周期
     *  値域は0以上32767以下
     *  0 ~ 32767を16進数に変換する
     */
    let pollingValue = isEnablePolling.value ? "01" : "00";
    const totalMinutes = Number(reportIntervalHours.value) * 60 + Number(reportIntervalMinutes.value);
    pollingValue += isEnablePolling.value ? totalMinutes.toString(16).padStart(4, "0") : "0000";
    return pollingValue;
  };

  const buildInstallParameters = () => {
    /** afterC9Paramsの設定値
     * Toolkit アプリケーション インスタンスの優先度: 255
     * タイマーの数: 5
     * チャネルの数: 5
     * メニューに表示するエントリーの数
     *    isEnableMenuDisplay.valueがtrue: 6
     *    isEnableMenuDisplay.valueがfalse: 0
     * メニュー エントリーの文字数: 32
     * ADF AID: A0000000871002FFFFFFFF8903020000
     * Volatile Memory Quota(C7): 1
     * タイマーの数: 1
     */
    const eaParams = isEnableMenuDisplay.value
      ? "EA2E8012FF052006010002000300040005000600050081180001000010A0000000871002FFFFFFFF8903020000010000"
      : "EA228006FF012000050081180001000010A0000000871002FFFFFFFF8903020000010000";
    const afterC9Params = `EF08C7020001C8020001${eaParams}`;
    let c9Value = "";
    c9Value += buildTLV(TAGS.FQDN, strToHexStream(fqdn.value));
    c9Value += buildTLV(TAGS.PORT, parseInt(port.value.toString(), 10).toString(16).padStart(4, "0"));
    c9Value += buildTLV(TAGS.ENCRYPT, `${padding(enableEncryption.value ? 1 : 0)}`);
    c9Value += buildTLV(TAGS.POLLING, buildPollingValue());
    const c9TLV = buildTLV(TAGS.C9, c9Value);
    return `${c9TLV}${afterC9Params}`.toUpperCase();
  };

  const padding = (value: string | number): string => {
    return `${value}`.padStart(2, "0");
  };

  const swap = (value: string): string => {
    let str = "";
    for (let offset = 0; offset < value.length; ) {
      str += value[offset + 1] + value[offset];
      offset += 2;
    }
    return str;
  };

  const strToHexStream = (str: string): string => {
    return str
      .split("")
      .map((c) => c.charCodeAt(0).toString(16))
      .join("")
      .toUpperCase();
  };

  const buildTLV = (tag: string, value: string | number): string => {
    const len = `${value}`.length / 2;
    return `${tag}${padding(len.toString(16))}${value}`;
  };

  return {
    encryptionItems,
    meta,
    fqdn,
    fqdnError,
    port,
    portError,
    reportIntervalHours,
    reportIntervalHoursError: _reportIntervalHoursError,
    reportIntervalMinutes,
    reportIntervalMinutesError: _reportIntervalMinutesError,
    enableEncryption,
    enableEncryptionError,
    initByInstallParameters,
    isValid,
    buildInstallParameters,
    isEnablePolling,
    isEnableMenuDisplay,
  };
}
